import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MultiSelectListProps } from '@node-space/ui-components'
import {
  PaymentMethod,
  PaymentMethodFormValue,
  RemoteWalletProviderType,
  type RemoteWalletProviderItem,
} from 'types/hppSettings'

export type MultiSelectListDetails = MultiSelectListProps['multiSelectListDetails']
export type WalletSection = MultiSelectListDetails['sections'][number]
export type WalletItem = WalletSection['items'][number]

export const paymentMethodsToFormValue = (
  methods: PaymentMethod[] = []
): PaymentMethodFormValue => {
  const hasQr = methods.includes(PaymentMethod.QR_CODE)
  const hasWallet = methods.includes(PaymentMethod.PAY_WITH_WALLET)
  if (hasQr && hasWallet) return PaymentMethodFormValue.BOTH
  if (hasWallet) return PaymentMethodFormValue.PAY_WITH_WALLET_ONLY
  return PaymentMethodFormValue.QR_CODE_ONLY
}

export const formValueToPaymentMethods = (value: PaymentMethodFormValue): PaymentMethod[] => {
  if (value === PaymentMethodFormValue.BOTH)
    return [PaymentMethod.QR_CODE, PaymentMethod.PAY_WITH_WALLET]
  if (value === PaymentMethodFormValue.PAY_WITH_WALLET_ONLY) return [PaymentMethod.PAY_WITH_WALLET]
  return [PaymentMethod.QR_CODE]
}

const toItems = (items: RemoteWalletProviderItem[]): WalletItem[] =>
  items.map(item => ({ id: item.provider.id, title: item.provider.name, checked: item.enabled }))

const emptyArr: RemoteWalletProviderItem[] = []

export const useWalletSections = (
  content: RemoteWalletProviderItem[] = emptyArr,
  searchTerm: string
) => {
  const [walletSections, setWalletSections] = useState<WalletSection[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    const exchanges = content.filter(
      item => item.provider.type === RemoteWalletProviderType.CUSTODIAL
    )
    const selfHosted = content.filter(
      item => item.provider.type === RemoteWalletProviderType.SELF_CUSTODY
    )

    const sections: WalletSection[] = []

    if (exchanges.length > 0) {
      sections.push({
        id: 'exchanges',
        title: t('hppSettings.exchanges'),
        description: t('hppSettings.countExchange', { count: exchanges.length }),
        items: toItems(exchanges),
      })
    }

    if (selfHosted.length > 0) {
      sections.push({
        id: 'self-hosted',
        title: t('hppSettings.selfHosted'),
        description: t('hppSettings.countWallet', { count: selfHosted.length }),
        items: toItems(selfHosted),
      })
    }

    setWalletSections(sections)
  }, [content, t])

  const filteredSections = useMemo<WalletSection[]>(() => {
    const term = searchTerm.toLowerCase()
    if (!term) return walletSections
    return walletSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => item.title.toLowerCase().includes(term)),
      }))
      .filter(section => section.items.length > 0)
  }, [walletSections, searchTerm])

  const selectedWalletCount = useMemo(
    () => walletSections.flatMap(s => s.items).filter(i => i.checked).length,
    [walletSections]
  )

  const sectionsWithCallbacks = useMemo<WalletSection[]>(
    () =>
      filteredSections.map(section => ({
        ...section,
        onSectionChange: (updatedSection: WalletSection) => {
          const updatedMap = new Map(updatedSection.items.map(i => [i.id, i.checked]))
          setWalletSections(prev =>
            prev.map(s => {
              if (s.id !== section.id) return s
              return {
                ...s,
                items: s.items.map(item => ({
                  ...item,
                  checked: updatedMap.has(item.id)
                    ? (updatedMap.get(item.id) as boolean)
                    : item.checked,
                })),
              }
            })
          )
        },
        items: section.items.map(item => ({
          ...item,
          onItemChange: (updatedItem: WalletItem) => {
            setWalletSections(prev =>
              prev.map(s => ({
                ...s,
                items: s.items.map(i =>
                  i.id === updatedItem.id ? { ...i, checked: updatedItem.checked } : i
                ),
              }))
            )
          },
        })),
      })),
    [filteredSections]
  )

  const handleSelectAllChange = (updatedSections: WalletSection[]) => {
    const updatedMap = new Map(updatedSections.flatMap(s => s.items.map(i => [i.id, i.checked])))
    setWalletSections(prev =>
      prev.map(section => ({
        ...section,
        items: section.items.map(item => ({
          ...item,
          checked: updatedMap.has(item.id) ? (updatedMap.get(item.id) as boolean) : item.checked,
        })),
      }))
    )
  }

  return {
    walletSections,
    selectedWalletCount,
    sectionsWithCallbacks,
    handleSelectAllChange,
  }
}

export const usePaymentOptions = () => {
  const { t } = useTranslation()
  const radioOptions = useMemo(
    () => [
      {
        value: PaymentMethodFormValue.QR_CODE_ONLY,
        label: t('hppSettings.paymentMethod.qrCodeOnly'),
        secondaryLabel: t('hppSettings.paymentMethod.qrCodeOnlyDescription'),
      },
      {
        value: PaymentMethodFormValue.PAY_WITH_WALLET_ONLY,
        label: t('hppSettings.paymentMethod.payWithWalletOnly'),
        secondaryLabel: t('hppSettings.paymentMethod.payWithWalletOnlyDescription'),
      },
      {
        value: PaymentMethodFormValue.BOTH,
        label: t('hppSettings.paymentMethod.both'),
        secondaryLabel: t('hppSettings.paymentMethod.bothDescription'),
      },
    ],
    [t]
  )

  return radioOptions
}
